import {
  Box,
  Chip,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { SEARCH_DEFAULT_ROWS_PER_PAGE } from '@shared/constants';
import type { ApiCreditsBalance } from '@shared/types/api-access';
import { InlineCode } from '~/components/InlineCode';

const CREDIT_COSTS: Array<{
  endpoint: string;
  cost: string;
  unitsPerCredit: number;
  unit: string;
}> = [
  {
    endpoint: '/search',
    cost: '1 credit per request',
    unitsPerCredit: 1,
    unit: 'searches',
  },
  {
    endpoint: '/domains',
    cost: '1 credit per domain',
    unitsPerCredit: 1,
    unit: 'domains',
  },
  {
    endpoint: '/downloads',
    cost: `1 credit per ${SEARCH_DEFAULT_ROWS_PER_PAGE} rows`,
    unitsPerCredit: SEARCH_DEFAULT_ROWS_PER_PAGE,
    unit: 'rows',
  },
];

interface ApiCreditsCardProps {
  credits: ApiCreditsBalance | null | undefined;
  loading: boolean;
}
export const ApiCreditsCard = ({ credits, loading }: ApiCreditsCardProps) => {
  if (!credits) {
    return loading ? <ApiCreditsCardSkeleton /> : null;
  }
  const remaining = credits.remaining;
  const percentRemaining = credits.monthly_credits
    ? Math.min((Math.max(remaining, 0) / credits.monthly_credits) * 100, 100)
    : 0;
  return (
    <Paper variant="outlined" sx={{ p: 4, height: '100%' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}>
        <Typography variant="h5">Free monthly credits</Typography>
        {remaining > 0 ? (
          <Chip label="Active" color="success" size="small" variant="filled" />
        ) : (
          <Chip
            label="No Balance Remaining"
            color="error"
            size="small"
            variant="filled"
          />
        )}
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mb: 1 }}>
        <Typography variant="h3" component="p" fontWeight={500}>
          {remaining.toLocaleString()}{' '}
          <Typography component="span" variant="h6" color="text.secondary">
            of {credits.monthly_credits.toLocaleString()} left
          </Typography>
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Resets{' '}
          {new Date(credits.reset_at).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={percentRemaining}
        color={remaining > 0 ? 'primary' : 'error'}
        sx={{ height: 10, borderRadius: 5, mb: 4 }}
      />
      <Typography variant="overline" color="text.secondary">
        Credit cost per endpoint
      </Typography>
      <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
        <Table size="small">
          <TableBody>
            {CREDIT_COSTS.map(({ endpoint, cost, unitsPerCredit, unit }) => {
              return (
                <TableRow
                  key={endpoint}
                  sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell sx={{ pl: 0, py: 1.5 }}>
                    <InlineCode>{endpoint}</InlineCode>
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Typography variant="body1">{cost}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 0, py: 1.5 }}>
                    <Typography variant="body1" color="text.secondary">
                      ≈{' '}
                      {(
                        Math.max(remaining, 0) * unitsPerCredit
                      ).toLocaleString()}{' '}
                      {unit}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
};

const ApiCreditsCardSkeleton = () => {
  return (
    <Paper variant="outlined" sx={{ p: 4, height: '100%' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}>
        <Typography variant="h5">Free monthly credits</Typography>
        <Skeleton variant="rounded" width={56} height={24} />
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mb: 1 }}>
        <Typography variant="h3" component="p">
          <Skeleton width={200} />
        </Typography>
        <Typography variant="body1">
          <Skeleton width={120} />
        </Typography>
      </Stack>
      <Skeleton variant="rounded" height={10} sx={{ borderRadius: 5, mb: 4 }} />
      <Typography variant="overline" color="text.secondary">
        Credit cost per endpoint
      </Typography>
      <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
        {CREDIT_COSTS.map(({ endpoint }) => {
          return <Skeleton key={endpoint} height={48} />;
        })}
      </Box>
    </Paper>
  );
};
